import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService, ConfigService, NoRestApiHelper } from '@services';
import { map, Observable } from 'rxjs';
import {
  IIntInvoiceRegister,
  IIntInvoiceRegisterCreatePayload,
  IIntInvoiceRegisterInsertPayload,
  IIntInvoiceRegisterPrintPayload,
  IIntInvoiceRegisterRemovePayload,
  IIntInvoiceRegisterUpdatePayload,
  IIntInvoiceRemoveFromRegisterPayload,
} from '../../models/invoice/int-invoice-register';
import { IListData } from '@models';
import * as _ from 'lodash';
import * as moment from 'moment/moment';
import { catchArrayBufferErrors, checkErrors } from '@shared';
import { PrintService } from '../print/print.service';
import { IInternationalInvoice } from '../../models/international-invoice';
import { RegisterCreateResponse, RegisterDeleteResponse } from '../../models/register.model';

@Injectable({
  providedIn: 'root',
})
export class IntRegistersApiService {

  constructor(
    protected http: HttpClient,
    protected configService: ConfigService,
    protected _noRestApiHelper: NoRestApiHelper,
    protected _authorizationService: AuthService,
    private printService: PrintService,
    private injector: Injector,
  ) {
  }

  create(payload: IIntInvoiceRegisterCreatePayload): Observable<RegisterCreateResponse> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'insertDocuments',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => checkErrors(data, this.injector, 'Помилка додавання')),
      map(data => (data as any).data?.[0]),
    );
  }

  insert(payload: IIntInvoiceRegisterInsertPayload): Observable<RegisterCreateResponse> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'insertDocuments',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => checkErrors(data, this.injector,'Помилка додавання')),
      map(data => (data as any).data?.[0]),
    );
  }

  update(payload: IIntInvoiceRegisterUpdatePayload): Observable<RegisterCreateResponse> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'updateScanSheet',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.bindListToModel(data)),
    );
  }

  getRegisters(pageIndex: number, pageSize: number): Observable<IListData<IIntInvoiceRegister>> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'getScanSheetList',
        methodProperties: {
          Page: pageIndex,
          Limit: pageSize,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.bindListToModel(data)),
    );
  }

  getInvoices(registerRef: string, page?: number, limit?: number, getFullList?: number): Observable<IListData<IInternationalInvoice>> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'getDocumentList',
        methodProperties: {
          State: 'allOrders',
          ScanSheetNumber: registerRef,
          ...(page ? { Page: page } : {}),
          ...(limit ? { Limit: limit } : {}),
          ...(getFullList ? { GetFullList: getFullList } : {}),
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.bindInvoicesListToModel(data)),
    );
  }

  delete(payload: IIntInvoiceRegisterRemovePayload): Observable<RegisterDeleteResponse> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'deleteScanSheet',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => checkErrors(data, this.injector, 'Помилка видалення')),
    );
  }

  print(payload: IIntInvoiceRegisterPrintPayload): Observable<any> {
    const observable = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternetDocument',
        calledMethod: 'printFull',
        methodProperties: {
          ...payload,
        },
      }, { responseType: 'arraybuffer' });

    return observable.pipe(
      map(response => catchArrayBufferErrors(response)),
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(response => response.data),
      map(data => {
        this.printService.openBlob(data, payload.Type);
        return null;
      }),
    );
  }

  removeFromRegister(payload: IIntInvoiceRemoveFromRegisterPayload): Observable<IListData<IIntInvoiceRegister>> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'removeDocuments',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.bindListToModel(data)),
    );
  }

  private bindListToModel(data: any): IListData<IIntInvoiceRegister> {
    return {
      list: _.map(data.data.data, (el: any) => {
        const invoiceRegister: IIntInvoiceRegister = {
          docNumber: el.Number,
          ref: el.Ref,
          printed: el.Printed,
          dateTime: el.DateTime ? moment(el.DateTime).toDate() : null,
          description: el.Description,
          count: el.Count,
        };
        return invoiceRegister;
      }),
      total: data.data.info.totalCount,
    };
  }

  private bindInvoicesListToModel(response: any): IListData<IInternationalInvoice> {
    return {
      list: response.data.data,
      total: response.data.info.totalCount,
    };
  }
}
